import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type { BuildingSavingsDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { DetailDates } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailDates";
import { DetailLoader } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLoader";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { DetailValueChartSection } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailValueChartSection";
import { RelatedSectionRedesign } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedSectionRedesign";
import { PortfolioBasicInfoRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/PortfolioBasicInfoRows";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { useNumberFormatter } from "web_myplann/Shared/Hooks/useNumberFormatter";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

export const BuildingSavingsRedesign: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const formatNumber = useNumberFormatter();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailLoader hasChart />;
	}

	const portfolioData = baseData.data as BuildingSavingsDetailDataDTO;

	const savings = portfolioData.payment
		? getPortfolioFrequencyValue(
				portfolioData.payment ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const formattedAppreciation = formatNumber(portfolioData.appreciation);
	const appreciation = formattedAppreciation && `${formattedAppreciation} %`;

	const stateContribution =
		typeof portfolioData.stateContribution !== "boolean"
			? ""
			: t(Resources.Common[portfolioData.stateContribution ? "yes" : "no"]);

	return (
		<>
			<DetailValueChartSection
				reporting={reporting}
				currencySign={currencySign}
				extraRows={
					<DetailRow
						label={t(Resources.PortfolioDetail.SharedLabels.targetAmount)}
						value={moneyFormatter(
							portfolioData.targetAmount,
							currencySign,
							"",
							2,
						)}
					/>
				}
			/>

			<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
				<PortfolioBasicInfoRows baseData={baseData} />
				<DetailRow
					label={t(Resources.PortfolioDetail.BuildingSavings.savings)}
					value={savings}
				/>
				<DetailRow
					label={t(Resources.PortfolioDetail.BuildingSavings.appreciation)}
					value={appreciation}
				/>
				<DetailRow
					label={t(Resources.PortfolioDetail.BuildingSavings.stateContribution)}
					value={stateContribution}
				/>
			</DetailCard>
			<DetailDates data={baseData} />

			<RelatedSectionRedesign
				documents={documents?.data}
				related={baseData.related}
				itemKind={PortfolioItemKind.Portfolio}
				portfolioId={baseData.id}
			/>
		</>
	);
};
